import React, { useState } from 'react';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ExportMenu, ReportHeader, ReportSkeleton, downloadCsv, exportPdf, tooltipStyle, CARD, LiveBadge } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Globe, Copy, Check, RefreshCw, Search } from 'lucide-react';
import apiV2 from '../utils/apiV2';
import { toast } from 'sonner';

const snippet = (orgId) =>
  `<script defer src="${window.location.origin}/beacon.js" data-bf-org="${orgId}" data-api="${process.env.REACT_APP_BACKEND_URL}/api"></script>`;

const BeaconSetup = ({ orgId, onInstalled }) => {
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null);
  const [siteUrl, setSiteUrl] = useState('');

  const copy = () => {
    navigator.clipboard.writeText(snippet(orgId)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const check = async (withSite = false) => {
    setChecking(true);
    try {
      const q = withSite && siteUrl ? `?checkUrl=${encodeURIComponent(siteUrl)}` : '';
      const res = await apiV2.get(`/organizations/${orgId}/intelligence/beacon-status${q}`);
      setStatus(res.data);
      if (res.data.installed) {
        toast.success('Beacon is live — events received!');
        setTimeout(onInstalled, 1200);
      }
    } catch {
      toast.error('Could not check beacon status');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className={`${CARD} p-6 sm:p-8`} data-testid="beacon-setup">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-11 h-11 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center shrink-0">
          <Globe className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div>
          <div className="font-extrabold text-slate-900 mb-1">Install the BookingFlows Beacon</div>
          <p className="text-sm text-slate-500 leading-relaxed max-w-xl">
            Paste this one-line tag into your website's <code className="bg-slate-100 px-1 rounded text-[12px]">&lt;head&gt;</code> (or just before <code className="bg-slate-100 px-1 rounded text-[12px]">&lt;/body&gt;</code>).
            It's ~1KB, fully anonymous, and starts reporting pageviews, referrers and how website visits turn into bookings.
          </p>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl p-4 mb-4 relative" data-testid="beacon-snippet">
        <code className="text-[12px] text-emerald-300 break-all leading-relaxed block pr-10 font-mono">{snippet(orgId)}</code>
        <button
          onClick={copy}
          data-testid="beacon-copy-btn"
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-4">
        <button
          onClick={() => check(false)}
          disabled={checking}
          data-testid="beacon-check-btn"
          className="px-4 py-2.5 rounded-xl bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
          Check if installed
        </button>
        <div className="flex flex-1 gap-2">
          <input
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="yourwebsite.com (optional — scan your site for the tag)"
            data-testid="beacon-site-url-input"
            className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <button
            onClick={() => check(true)}
            disabled={checking || !siteUrl}
            data-testid="beacon-scan-btn"
            className="px-3 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200 transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            <Search className="w-4 h-4" /> Scan site
          </button>
        </div>
      </div>

      {status && (
        <div
          className={`rounded-xl p-4 text-sm ${status.installed ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100' : 'bg-amber-50 text-amber-800 ring-1 ring-amber-100'}`}
          data-testid="beacon-status-result"
        >
          {status.installed ? (
            <><strong>🟢 Beacon is live!</strong> {status.totalPageviews.toLocaleString()} pageviews received{status.lastEventAt ? ` — last event ${new Date(status.lastEventAt).toLocaleString()}` : ''}. Loading your report…</>
          ) : (
            <>
              <strong>No events received yet.</strong> Make sure the tag is deployed, then open your website once and re-check.
              {status.siteCheck && (
                <div className="mt-1.5">
                  {status.siteCheck.reachable
                    ? status.siteCheck.tagFound
                      ? '✅ Tag found on your site — waiting for the first pageview to arrive.'
                      : status.siteCheck.tagPresent
                        ? '⚠️ A beacon tag was found, but with a different org ID — double-check the data-bf-org value.'
                        : `❌ Tag not found on ${status.siteCheck.url}.`
                    : `❌ Could not reach ${status.siteCheck.url}.`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default function WebsiteAnalyticsReport() {
  const [months, setMonths] = useState(3);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSnippet, setShowSnippet] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const { data, loading } = useIntelligenceReport(`website-analytics`, months + refreshKey * 0);
  const orgId = localStorage.getItem('organizationId');

  const copyId = () => {
    navigator.clipboard.writeText(orgId || '').then(() => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    });
  };

  const exportCsv = () => {
    if (!data?.beaconInstalled) return;
    downloadCsv([
      ['Date', 'Pageviews', 'Visitors'],
      ...data.dailyTrend.map((d) => [d.date, d.pageviews, d.visitors]),
      [],
      ['Page', 'Views', 'Visitors'],
      ...data.topPages.map((p) => [p.path, p.views, p.visitors]),
      [],
      ['Referrer', 'Sessions'],
      ...data.referrers.map((r) => [r.source, r.sessions]),
    ], 'website-analytics.csv');
  };
  const exportPdfDoc = () => {
    if (!data?.beaconInstalled) return;
    exportPdf({
      filename: 'website-analytics.pdf',
      title: 'Website Analytics',
      subtitle: `Last ${months} months`,
      kpis: [
        ['Pageviews', String(data.kpis.pageviews)],
        ['Unique visitors', String(data.kpis.uniqueVisitors)],
        ['Sessions', String(data.kpis.sessions)],
        ['Widget engagement', `${data.kpis.widgetEngagementPct}%`],
      ],
      tables: [{
        title: 'Top pages',
        head: ['Page', 'Views', 'Visitors'],
        rows: data.topPages.map((p) => [p.path, p.views, p.visitors]),
      }],
    });
  };

  return (
    <IntelligenceShell>
      <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto" data-testid="website-analytics-report">
        <ReportHeader
          title="Website Analytics"
          blurb="Your own BookingFlows Beacon — pageviews, visitors, referrers and how website traffic turns into widget sessions and bookings."
          right={data?.beaconInstalled ? <><AiSuggestion report="website-analytics" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="website" /><ExportMenu onCsv={exportCsv} onPdf={exportPdfDoc} testid="website-export" /></> : null}
        />
        {loading ? <ReportSkeleton /> : !data?.beaconInstalled ? (
          <BeaconSetup orgId={orgId} onInstalled={() => setRefreshKey((k) => k + 1)} />
        ) : (
          <>
            <div className={`${CARD} px-4 py-2.5 flex items-center gap-3 flex-wrap mb-4`} data-testid="beacon-live-strip">
              <LiveBadge />
              <span className="text-xs text-slate-500">Beacon installed &amp; firing</span>
              <span className="hidden sm:flex items-center gap-1.5">
                <span className="text-[11px] font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded-md" data-testid="beacon-id-value">{orgId}</span>
                <button onClick={copyId} data-testid="beacon-id-copy" className="text-slate-300 hover:text-slate-500 transition-colors">
                  {idCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                </button>
              </span>
              <button
                onClick={() => setShowSnippet((v) => !v)}
                data-testid="beacon-snippet-toggle"
                className="text-[11px] text-slate-400 hover:text-slate-600 underline decoration-slate-200 underline-offset-2 transition-colors"
              >
                {showSnippet ? 'hide snippet' : 'snippet'}
              </button>
              <span className="ml-auto text-[11px] text-slate-400">
                last fired {data.lastEventAt ? new Date(data.lastEventAt).toLocaleString() : '—'}
              </span>
            </div>
            {showSnippet && (
              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3 mb-4 flex items-center gap-3" data-testid="beacon-snippet-row">
                <code className="text-[11px] font-mono text-slate-500 break-all flex-1">{snippet(orgId)}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(snippet(orgId)); setShowSnippet(false); }}
                  className="text-slate-400 hover:text-slate-600 shrink-0 transition-colors"
                  data-testid="beacon-snippet-copy"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <KpiCard label="Pageviews" value={data.kpis.pageviews.toLocaleString()} testid="web-kpi-pageviews" />
              <KpiCard label="Unique visitors" value={data.kpis.uniqueVisitors.toLocaleString()} testid="web-kpi-visitors" delay={40} />
              <KpiCard label="Sessions" value={data.kpis.sessions.toLocaleString()} sub={`${data.kpis.pagesPerSession} pages / session`} testid="web-kpi-sessions" delay={80} />
              <KpiCard
                label="Visitors → widget"
                value={`${data.kpis.widgetEngagementPct}%`}
                sub={`${data.kpis.widgetEngagedVisitors} engaged · ${data.kpis.completedBookings} bookings`}
                testid="web-kpi-engagement"
                delay={120}
              />
            </div>

            <ChartCard title="Daily traffic" testid="web-trend" className="mb-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.dailyTrend}>
                    <defs>
                      <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} minTickGap={40} />
                    <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={34} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area dataKey="pageviews" name="Pageviews" stroke="#2563EB" strokeWidth={2} fill="url(#pvGrad)" />
                    <Area dataKey="visitors" name="Visitors" stroke="#60A5FA" strokeWidth={1.5} fill="none" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
              <ChartCard title="Top pages" testid="web-pages">
                <table className="w-full text-sm">
                  <tbody>
                    {data.topPages.map((p) => (
                      <tr key={p.path} className="border-b border-slate-50">
                        <td className="py-2 pr-2 font-mono text-[12px] text-slate-700 max-w-[220px] truncate">{p.path}</td>
                        <td className="py-2 px-2 text-right"><MonoNum className="font-bold">{p.views.toLocaleString()}</MonoNum></td>
                        <td className="py-2 pl-2 text-right text-xs text-slate-400"><MonoNum>{p.visitors}</MonoNum> visitors</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ChartCard>
              <ChartCard title="Where visitors come from" testid="web-referrers">
                <div className="space-y-1.5 mb-5">
                  {data.referrers.map((r) => {
                    const max = data.referrers[0]?.sessions || 1;
                    return (
                      <div key={r.source} className="flex items-center gap-3">
                        <div className="w-32 text-xs font-semibold text-slate-600 truncate">{r.source}</div>
                        <div className="flex-1 h-4 bg-slate-100/70 rounded overflow-hidden">
                          <div className="h-full bg-[#60A5FA] rounded" style={{ width: `${Math.max(3, (100 * r.sessions) / max)}%` }} />
                        </div>
                        <MonoNum className="w-12 text-xs font-bold text-slate-700 text-right">{r.sessions}</MonoNum>
                      </div>
                    );
                  })}
                </div>
                {data.utmSources.length > 0 && (
                  <>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Campaigns (UTM)</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {data.utmSources.map((u, i) => (
                          <tr key={i} className="border-b border-slate-50">
                            <td className="py-1.5 pr-2 font-semibold text-slate-700">{u.source}</td>
                            <td className="py-1.5 px-2 text-slate-400">{u.medium}</td>
                            <td className="py-1.5 pl-2 text-right"><MonoNum className="font-bold">{u.views}</MonoNum></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
                <div className="mt-4 flex gap-2 flex-wrap">
                  {Object.entries(data.devices).map(([d, n]) => (
                    <span key={d} className="px-2.5 py-1 rounded-full bg-slate-50 ring-1 ring-slate-100 text-[11px] font-semibold text-slate-600">
                      {d}: <MonoNum className="font-bold">{n.toLocaleString()}</MonoNum>
                    </span>
                  ))}
                </div>
              </ChartCard>
            </div>

            <div className="text-[11px] text-slate-400" data-testid="web-last-event">
              Fully anonymous — no cookies for tracking individuals
            </div>
          </>
        )}
      </div>
    </IntelligenceShell>
  );
}
